import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * Which deployment am I looking at.
 *
 * Trivial to render and the whole reason it exists is not trivial: from the
 * moment there is more than one stack, every console tab looks identical, and
 * the mistakes that follow are the expensive kind — authoring into the wrong
 * catalogue, or running a destructive action against the environment an
 * authoring team is using. A hostname in the URL bar is not an answer, because
 * `staging-nafis` and `nafis` differ by one word in a place nobody reads twice.
 *
 * READ FROM `/api/health`, NOT FROM A BUILD-TIME CONSTANT. The console is a
 * static bundle served by nginx and the same image is deployed to both stacks —
 * a `VITE_ENVIRONMENT` baked at build time would mean two images, and the badge
 * would then be asserting what the bundle *was built for* rather than what it
 * is *actually talking to*. Reading it from the API it calls makes it a
 * measurement: if the console is proxying to staging's API, it says staging,
 * because that is the thing that would actually be modified.
 *
 * PRODUCTION IS DELIBERATELY UNLABELLED. A badge on every screen everywhere
 * becomes furniture and stops being read; the signal is worth more if it only
 * appears when you are somewhere that is *not* the real thing.
 */
const TONE: Record<string, string> = {
  staging: 'bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-400',
  development: 'bg-sky-500/15 text-sky-700 ring-sky-500/30 dark:text-sky-400',
};

export function EnvironmentBadge() {
  const t = useT();
  const health = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    // It changes when the deployment changes, which is never within a session.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const environment = health.data?.environment;
  if (!environment || environment === 'production') return null;

  return (
    <span
      // `title` rather than a tooltip component: this must render identically
      // on a screen that has failed to load anything else.
      // The environment NAME is not translated — `development` and `staging`
      // are the values `/api/health` reports and the names the compose projects
      // carry, and an operator reading a log or an ssh session must see the same
      // word the badge showed them.
      title={t('chrome.environmentTitle', { environment })}
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium tracking-wide uppercase ring-1 ring-inset',
        TONE[environment] ?? 'bg-muted text-muted-foreground ring-border',
      )}
    >
      {environment}
    </span>
  );
}
